/* eslint-disable spire/export-styles */
import Zone from "@insite/client-framework/Components/Zone";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
    cssClass = "cssClass",
}

export interface RowStyles {
    wrapper?: InjectableCss;
}

export const rowStyles: RowStyles = {
    wrapper: {
        css: css`
            &.blueBg {
                background: ${getColor("common.accent")};
            }

            &.gradientBg {
                background: transparent
                    linear-gradient(180deg, ${getColor("primary")} 0%, ${getColor("secondary")} 100%) 0% 0% no-repeat
                    padding-box;
            }

            &.rowSpacing {
                padding: 50px 0;
            }

            &.border {
                border-radius: 10px;
            }

            &.blueBorder {
                border-top: 7px solid ${getColor("primary")};
            }

            &.signin-row div[class^="GridItemStyle"] > div {
                height: 100%;
            }

            &.bannerWrapper {
                position: relative;
                h1,
                h2,
                h3 {
                    color: ${getColor("white")};
                }
            }

            &.bottomSpace {
                padding-bottom: 40px;
            }

            &.myAccountSettings {
                background: ${getColor("common.accent")};
                border-radius: 10px;
                padding: 20px;
            }

            &.centerRow {
                margin: 0 auto;
            }

            &.myAccountSettingsInner > div[class^="GridOffset"] > div[class^="GridItemStyle"] > div[class^="Wrapper"] {
                background: ${getColor("common.accent")};
                border-radius: 10px;
                padding: 20px;
            }

            &.themeRow {
                background: ${getColor("common.accent")};
                border-top: 7px solid ${getColor("primary")};
                border-radius: 10px;
                padding: 50px 20px;
            }

            &.SignInCustomRow > div[class^="GridOffset-sc"] {
                > div[class^="GridItemStyle-sc"] {
                    > div[class^="Wrapper-sc"] {
                        > div[class^="GridWrapper-sc"] {
                            > div[class^="GridOffset-sc"] {
                                > div[class^="GridItemStyle-sc"]:last-child {
                                    > div[class^="Wrapper-sc"] {
                                        height: 100%;
                                        background: #f3efeb;
                                        border-radius: 15px 15px 8px 8px;
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `,
    },
};

const styles = rowStyles;

interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.cssClass]: string;
    };
}

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [3, 3, 1, 1, 1],
    2: [4, 4, 2, 2, 2],
    3: [6, 6, 3, 3, 3],
    4: [6, 6, 4, 4, 4],
    5: [12, 12, 5, 5, 5],
    6: [12, 12, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

const Row: React.FunctionComponent<Props> = (props: Props) => {
    const {
        fields: { columns, gap, alignment, reflow, cssClass },
        id,
    } = props;

    const columnElements: JSX.Element[] = [];

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] ? alignment[x] : "top";
        columnElements.push(
            <GridItem key={x} width={reflow ? convertToResponsive(columns[x]) : columns[x]} align={align}>
                <Zone contentId={id} zoneName={`Content${x}`} />
            </GridItem>,
        );
    }

    return (
        <GridContainer className={cssClass} gap={gap || 0} {...styles.wrapper}>
            {columnElements}
        </GridContainer>
    );
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Row,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "ColumnsField",
                defaultValue: [4, 4, 4],
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 10,
            },
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "ColumnAlignmentField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
